import type { SectionProps } from "../types";

interface HeroSectionProps extends SectionProps {
  onScrollToRoadmap?: () => void;
  onScrollToCourses?: () => void;
}

export default function HeroSection({ isVisible, sectionRef, onScrollToRoadmap, onScrollToCourses }: HeroSectionProps) {
  return (
    <section
      ref={sectionRef}
      data-section="hero"
      className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-[#2a4b7c] via-[#1e3a5f] to-[#2a4b7c] pt-16 sm:pt-20 md:pt-24"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-96 h-96 bg-cyan-400 rounded-full blur-3xl animate-morph-1"></div>
        <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-cyan-400 rounded-full blur-3xl animate-morph-2"></div>
      </div>

      {/* Floating Particles & Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Particles */}
        <div
          className="absolute w-2 h-2 bg-cyan-400/30 rounded-full animate-float-slow"
          style={{ top: "20%", left: "10%" }}
        ></div>
        <div
          className="absolute w-3 h-3 bg-cyan-400/20 rounded-full animate-float-medium"
          style={{ top: "60%", left: "15%" }}
        ></div>
        <div
          className="absolute w-2 h-2 bg-cyan-400/40 rounded-full animate-float-fast"
          style={{ top: "40%", right: "20%" }}
        ></div>
        <div
          className="absolute w-4 h-4 bg-cyan-400/15 rounded-full animate-float-slow"
          style={{ top: "70%", right: "30%" }}
        ></div>
        <div
          className="absolute w-2 h-2 bg-blue-400/25 rounded-full animate-float-medium"
          style={{ top: "30%", left: "25%" }}
        ></div>

        {/* Decorative Icons */}
        <div
          className="absolute text-cyan-400/10 text-6xl animate-float-slow"
          style={{ top: "15%", right: "15%" }}
        >
          <i className="ri-code-s-slash-line"></i>
        </div>
        <div
          className="absolute text-cyan-400/10 text-5xl animate-float-medium"
          style={{ bottom: "20%", left: "10%" }}
        >
          <i className="ri-terminal-box-line"></i>
        </div>
        <div
          className="absolute text-cyan-400/10 text-4xl animate-float-fast"
          style={{ top: "40%", left: "5%" }}
        >
          <i className="ri-braces-line"></i>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div
            className={`transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-12"
            }`}
          >
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-[#3d5a8c] rounded-full mb-4 sm:mb-6">
              <span className="text-cyan-300 text-[10px] sm:text-xs font-bold tracking-wide uppercase">
                Đường dài cần nền móng sâu
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-4 sm:mb-6">
              Trở thành thế hệ
              <br />
              <span className="relative inline-block pt-2">
                <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent bg-[length:200%_auto] animate-text-shimmer">
                  kỹ sư công nghệ
                </span>
                {/* Glow effect */}
                <span className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 via-blue-400/20 to-cyan-400/20 blur-xl animate-pulse"></span>
              </span>
              <br />
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent bg-[length:200%_auto] animate-text-shimmer">
                  tiếp theo
                </span>
              </span>
            </h1>

            <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-6 sm:mb-8 leading-relaxed">
              Đào tạo lập trình và AI thực chiến dành cho những người khao khát
              đẩy nút kỹ nguyên số. Học từ dự án thật, làm việc tại công ty
              thật.
            </p>

            {/* Stats/Features */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 sm:mb-8">
              <div className="bg-white/5 backdrop-blur-sm border border-cyan-500/20 rounded-lg p-2 sm:p-3 hover:border-cyan-500/50 transition-all duration-300">
                <div className="text-cyan-400 text-lg sm:text-2xl font-bold mb-0.5 sm:mb-1">
                  500+
                </div>
                <div className="text-gray-400 text-[10px] sm:text-xs">Học viên</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-cyan-500/20 rounded-lg p-2 sm:p-3 hover:border-cyan-500/50 transition-all duration-300">
                <div className="text-cyan-400 text-lg sm:text-2xl font-bold mb-0.5 sm:mb-1">95%</div>
                <div className="text-gray-400 text-[10px] sm:text-xs">Có việc làm</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-cyan-500/20 rounded-lg p-2 sm:p-3 hover:border-cyan-500/50 transition-all duration-300">
                <div className="text-cyan-400 text-lg sm:text-2xl font-bold mb-0.5 sm:mb-1">
                  4.9★
                </div>
                <div className="text-gray-400 text-[10px] sm:text-xs">Đánh giá</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
              <button
                onClick={onScrollToCourses}
                className="px-6 sm:px-8 py-3 sm:py-3.5 bg-cyan-500 text-white text-sm sm:text-base font-bold rounded-lg hover:bg-cyan-400 transition-all duration-300 hover:scale-105 shadow-lg text-center"
              >
                Khám phá khóa học
              </button>
              <button
                onClick={onScrollToRoadmap}
                className="px-6 sm:px-8 py-3 sm:py-3.5 border-2 border-white text-white text-sm sm:text-base font-bold rounded-lg hover:bg-white hover:text-[#2a4b7c] transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
              >
                Xem lộ trình
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            {/* Feature badges */}
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <div className="flex items-center gap-1.5 sm:gap-2 text-gray-300 text-xs sm:text-sm">
                <i className="ri-check-line text-cyan-400"></i>
                <span>Dự án thực tế</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 text-gray-300 text-xs sm:text-sm">
                <i className="ri-check-line text-cyan-400"></i>
                <span>Mentor 1-1</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 text-gray-300 text-xs sm:text-sm">
                <i className="ri-check-line text-cyan-400"></i>
                <span>Cam kết việc làm</span>
              </div>
            </div>
          </div>

          {/* Right Image - Code Editor Mockup */}
          <div
            className={`transition-all duration-1000 delay-300 mt-8 lg:mt-0 ${
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-12"
            }`}
          >
            <div className="relative">
              {/* Glowing border effect with animation */}
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 rounded-3xl blur-2xl animate-pulse"></div>

              {/* Floating badge */}
              <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 z-20 bg-gradient-to-r from-cyan-500 to-blue-500 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full shadow-lg animate-bounce">
                <span className="text-white text-[10px] sm:text-xs font-bold">
                  ⚡ Live Coding
                </span>
              </div>

              {/* Code Editor Mockup */}
              <div className="relative bg-[#0d1117] rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden border border-cyan-500/20 hover:border-cyan-500/40 transition-all duration-300">
                {/* Browser Header */}
                <div className="bg-[#161b22] px-3 sm:px-4 py-2 sm:py-3 flex items-center gap-2 border-b border-gray-700">
                  <div className="flex gap-1.5 sm:gap-2">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-red-500"></div>
                    <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="ml-2 sm:ml-4 text-gray-400 text-[10px] sm:text-xs">main.tsx</div>
                </div>

                {/* Code Content */}
                <div className="p-4 sm:p-6 font-mono text-xs sm:text-sm">
                  <div className="space-y-2">
                    <div className="text-gray-500 hover:bg-white/5 px-2 py-1 -mx-2 rounded transition-colors">
                      <span className="text-purple-400">import</span>
                      <span className="text-gray-300"> React </span>
                      <span className="text-purple-400">from</span>
                      <span className="text-green-400"> 'react'</span>;
                    </div>
                    <div className="text-gray-500 hover:bg-white/5 px-2 py-1 -mx-2 rounded transition-colors">
                      <span className="text-purple-400">import</span>
                      <span className="text-gray-300"> useState </span>
                      <span className="text-purple-400">from</span>
                      <span className="text-green-400"> 'react'</span>;
                    </div>
                    <div className="h-4"></div>
                    <div className="text-gray-500 hover:bg-white/5 px-2 py-1 -mx-2 rounded transition-colors">
                      <span className="text-purple-400">const</span>
                      <span className="text-blue-400"> AcademyPage</span>
                      <span className="text-gray-300"> = () </span>
                      <span className="text-purple-400">=&gt;</span>
                      <span className="text-gray-300"> {"{"}</span>
                    </div>
                    <div className="text-gray-500 hover:bg-white/5 px-2 py-1 -mx-2 rounded transition-colors pl-4">
                      <span className="text-purple-400">const</span>
                      <span className="text-gray-300"> [</span>
                      <span className="text-cyan-400">course</span>
                      <span className="text-gray-300">, </span>
                      <span className="text-cyan-400">setCourse</span>
                      <span className="text-gray-300">] = </span>
                      <span className="text-blue-400">useState</span>
                      <span className="text-gray-300">(</span>
                      <span className="text-green-400">''</span>
                      <span className="text-gray-300">);</span>
                    </div>
                    <div className="h-4"></div>
                    <div className="text-gray-500 hover:bg-white/5 px-2 py-1 -mx-2 rounded transition-colors pl-4">
                      <span className="text-purple-400">return</span>
                      <span className="text-gray-300"> (</span>
                    </div>
                    <div className="text-gray-500 hover:bg-white/5 px-2 py-1 -mx-2 rounded transition-colors pl-8">
                      <span className="text-gray-300">&lt;</span>
                      <span className="text-green-400">div</span>
                      <span className="text-cyan-400"> className</span>
                      <span className="text-gray-300">=</span>
                      <span className="text-orange-400">"academy"</span>
                      <span className="text-gray-300">&gt;</span>
                    </div>
                    <div className="text-gray-500 hover:bg-white/5 px-2 py-1 -mx-2 rounded transition-colors pl-12 bg-cyan-500/10 border-l-2 border-cyan-500">
                      <span className="text-gray-300">&lt;</span>
                      <span className="text-green-400">h1</span>
                      <span className="text-gray-300">&gt;</span>
                      <span className="text-gray-300">Welcome</span>
                      <span className="text-gray-300">&lt;/</span>
                      <span className="text-green-400">h1</span>
                      <span className="text-gray-300">&gt;</span>
                      <span className="inline-block w-2 h-4 bg-cyan-400 ml-1 animate-pulse"></span>
                    </div>
                    <div className="text-gray-500 hover:bg-white/5 px-2 py-1 -mx-2 rounded transition-colors pl-8">
                      <span className="text-gray-300">&lt;/</span>
                      <span className="text-green-400">div</span>
                      <span className="text-gray-300">&gt;</span>
                    </div>
                    <div className="text-gray-500 hover:bg-white/5 px-2 py-1 -mx-2 rounded transition-colors pl-4">
                      <span className="text-gray-300">);</span>
                    </div>
                    <div className="text-gray-500 hover:bg-white/5 px-2 py-1 -mx-2 rounded transition-colors">
                      <span className="text-gray-300">{"}"}</span>
                    </div>
                  </div>

                  {/* Line numbers on hover */}
                  <div className="absolute left-2 top-20 text-gray-600 text-xs space-y-2 select-none opacity-50">
                    <div>1</div>
                    <div>2</div>
                    <div>3</div>
                    <div>4</div>
                    <div>5</div>
                    <div>6</div>
                    <div>7</div>
                    <div>8</div>
                    <div>9</div>
                    <div>10</div>
                    <div>11</div>
                    <div>12</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
        <div className="flex flex-col items-center gap-1 sm:gap-2">
          <span className="text-white/60 text-[10px] sm:text-xs uppercase tracking-wider">
            Cuộn xuống
          </span>
          <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white/30 rounded-full flex justify-center p-1">
            <div className="w-1 h-2 sm:h-3 bg-white/50 rounded-full animate-scroll"></div>
          </div>
        </div>
      </div>
    </section>
  );
}

